import type { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { ExplanationItem } from './ExplanationItem'
import { TalkList } from './TalkList'
import { talkItemList } from './talkLists'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { CompanyLinks } from '@/components/layouts/Company/CompanyLinks'
import { Head } from '@/components/layouts/Head'

export const PhilosophyPage = () => {
  const explanationItemList = [
    {
      mainTitle: 'Purpose',
      text: <>世界の誰もが「豊かさ」を享受できる 社会をつくる。</>,
    },
    {
      mainTitle: 'Vision',
      text: <>テクノロジーと、人の経験知で、 世界のリアルコマースを変える。</>,
    },
    {
      mainTitle: 'Value',
      text: (
        <>
          <p>・効率化された店舗網で、モノを流通させる力</p>
          <p>・データとIoTを駆使する力</p>
        </>
      ),
    },
  ]

  return (
    <>
      <Head
        title="経営理念"
        description="トライアルホールディングスの経営理念を掲載しています。"
        ogp={{ type: 'article', url: 'company/philosophy/' }}
      />
      <PhilosophyMainWrapper>
        <PhilosophyMain>
          <SectionTitle
            titleText="Philosophy"
            subTitleText="経営理念"
            color="normal"
          />
          <Philosophy>
            <h3>当社グループのパーパス・ビジョン・バリュー</h3>
            <PhilosophyTriangle>
              <ImageWrapper>
                <img
                  src="/images/Philosophy_JP.svg"
                  alt="TRIAL Holdingsのパーパス・ビジョン・バリュー"
                />
              </ImageWrapper>
              <Explanation>
                {explanationItemList.map(({ mainTitle, text }) => (
                  <ExplanationItem
                    key={mainTitle}
                    mainTitle={mainTitle}
                    text={text}
                  />
                ))}
              </Explanation>
            </PhilosophyTriangle>
          </Philosophy>
          <SpecialTalk>
            <h3>
              Special Talk<span>スペシャル対談</span>
            </h3>
            <NameWrapper>
              <Name data-left>
                <p>取締役 石橋 亮太</p>
              </Name>
              <Name data-right>
                <p>取締役 永田 洋幸</p>
              </Name>
            </NameWrapper>
            <Spacer h={{ sp: 60, pc: 70 }} />
            <img src="/images/philosophy-talk.webp" alt="PhilosophyTalk画像" />
            <Spacer h={{ sp: 34, pc: 60 }} />
          </SpecialTalk>
          <TalkMain>
            <TalkLead>
              <h3>「らしさ」を込めた策定秘話</h3>
              <p>
                企業経営において、自社の存在意義を明確にしながら、社会に与える価値を示す「パーパス」が注目を集めています。さらに、自社の価値観や目指す姿を「ビジョン」や「バリュー」として言語化することで、組織の一体感を醸成する役割を果たします。
              </p>
              <p>
                トライアルホールディングスにおける「パーパス・ビジョン・バリュー」は、経営陣が議論を重ねながら、それぞれの熱い思いをカタチにしたものです。
                <br />
                取締役 石橋 亮太、取締役 永田
                洋幸に策定秘話をインタビューしました。
              </p>
            </TalkLead>
            <Spacer h={{ sp: 56, pc: 110 }} />
            <Talk>
              {talkItemList.map(({ talkTitle, talkList }) => (
                <TalkList
                  key={talkTitle}
                  talkTitle={talkTitle}
                  talkList={talkList}
                />
              ))}
            <div><p style={{ textAlign: 'right' }}>2022年12月</p></div>
            </Talk>
          </TalkMain>
        </PhilosophyMain>
        <CompanyLinks padding={{ pc: '130px 0 0' }} />
      </PhilosophyMainWrapper>
    </>
  )
}

PhilosophyPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Corporate Information',
    subTitle: '企業情報',
  }

  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '企業情報', path: '/company/' },
    { name: '経営理念', path: '' },
  ]

  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const PhilosophyMainWrapper = styled.div`
  width: 1040px;
  padding-bottom: 130px;
  margin: 0 auto 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 0 auto 0;
    padding: 0 20px 70px;
  }
`

const PhilosophyMain = styled.div`
  margin-top: 78px;

  @media screen and (max-width: 1047px) {
    margin-top: 40px;
  }
`

const Philosophy = styled.section`
  margin-top: 132px;
  background-color: ${theme.colors.backgroundGrey};
  padding: 70px;

  h3 {
    color: ${theme.colors.trialBlue};
    font-size: 28px;
    font-weight: bold;
    text-align: center;
    line-height: 1.2;
  }

  @media screen and (max-width: 1047px) {
    margin-top: 70px;
    padding: 40px 20px;

    h3 {
      font-size: 20px;
    }
  }
`

const PhilosophyTriangle = styled.div`
  margin-top: 80px;
  display: flex;
  gap: 80px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: center;
    margin-top: 40px;
    gap: 40px;
  }
`

const ImageWrapper = styled.div`
  width: 440px;
  height: 340px;

  @media screen and (max-width: 1047px) {
    width: 295px;
    height: 228px;

    img {
      width: 100%;
      height: 100%;
    }
  }
`

const Explanation = styled.div``

const SpecialTalk = styled.section`
  margin-top: 130px;

  h3 {
    font-size: 22px;
    color: #3786c7;
    line-height: 32px;
    text-align: center;
    font-family: ${theme.fontEn};
  }

  span {
    display: block;
    font-size: 18px;
    line-height: 1;
    color: ${theme.colors.textTitle};
  }

  img {
    width: 100%;
  }

  @media screen and (max-width: 1047px) {
    margin-top: 65px;

    h3 {
      font-size: 18px;
    }

    span {
      font-size: 12px;
    }
  }
`

const NameWrapper = styled.div`
  margin-top: 46px;
  display: flex;
  justify-content: center;
  gap: 93px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: center;
    gap: 49px;
  }
`

const Name = styled.div`
  width: 274px;
  height: 64px;
  padding: 17px 25px;
  position: relative;

  p {
    color: #fff;
    font-size: 28px;
    line-height: 1;
  }

  &[data-left] {
    background-color: #3786c7;

    &::after {
      content: '';
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 32px 0 32px 30px;
      border-color: transparent transparent transparent #3786c7;
      display: inline-block;
      position: absolute;
      right: -30px;
      top: 0;
    }

    &::before {
      content: '';
      height: 2px;
      width: 90px;
      background-color: #3786c7;
      transform: rotate(45deg);
      display: inline-block;
      position: absolute;
      top: 31px;
      right: -91px;
    }
  }

  &[data-right] {
    background-color: ${theme.colors.trialBlue};

    &::after {
      content: '';
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 32px 30px 32px 0;
      border-color: transparent ${theme.colors.trialBlue} transparent
        transparent;
      display: inline-block;
      position: absolute;
      left: -30px;
      top: 0;
    }

    &::before {
      content: '';
      height: 2px;
      width: 90px;
      background-color: ${theme.colors.trialBlue};
      transform: rotate(-45deg);
      display: inline-block;
      position: absolute;
      top: 31px;
      left: -91px;
    }
  }

  @media screen and (max-width: 1047px) {
    height: 50px;
    padding: 13px 50px 12px;

    p {
      font-size: 22px;
    }

    &[data-left]::after,
    &[data-right]::after {
      display: none;
    }

    &[data-left]::before {
      width: 35px;
      top: 85px;
      left: 50%;
      transform: rotate(45deg) translateX(-50%);
    }

    &[data-right]::before {
      width: 35px;
      top: -39px;
      left: 50%;
      transform: rotate(-45deg) translateX(-50%);
    }
  }
`

const TalkMain = styled.section``

const TalkLead = styled.div`
  h3 {
    color: ${theme.colors.trialBlue};
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 40px;
  }

  p {
    font-size: 18px;
    line-height: 2;

    & + p {
      margin-top: 30px;
    }
  }

  @media screen and (max-width: 1047px) {
    h3 {
      font-size: 20px;
      margin-bottom: 20px;
    }

    p {
      font-size: 15px;
      line-height: 1.8;

      & + p {
        margin-top: 20px;
      }
    }
  }
`

const Talk = styled.div``
